import { assertSnapshotInvariants, validateSnapshot } from "./invariants";
import {
  cellKey,
  cloneArenaMap,
  freezeArenaMap,
  generateMap,
  getSpawnPoints,
  getTile,
  isInside,
  isWalkableTile,
  type MutableArenaMap,
} from "./map";
import { hashCanonical } from "./canonical";
import { hashSeed, mixSeed, SeededRng } from "./rng";
import {
  DEFAULT_RULES,
  POSITION_SCALE,
  type ArenaMap,
  type BombSnapshot,
  type Cell,
  type Direction,
  type FlameSnapshot,
  type GameConfig,
  type GameEvent,
  type GameRules,
  type GameSnapshot,
  type InputCommand,
  type InputFrame,
  type InvariantViolation,
  type ItemPlacement,
  type ItemSnapshot,
  type NormalizedInputCommand,
  type PlayerConfig,
  type PlayerId,
  type PlayerInput,
  type PlayerSnapshot,
  type ReplayData,
  type ReplayTick,
  type RoundStatus,
  type Seed,
  type TickInputFrame,
} from "./types";

interface MutablePoint {
  x: number;
  y: number;
}

interface MutablePlayer {
  id: PlayerId;
  name?: string;
  position: MutablePoint;
  previousPosition: MutablePoint;
  facing: Direction;
  speedLevel: number;
  maxBombs: number;
  bombRange: number;
  kick: boolean;
  remote: boolean;
  pierce: boolean;
  shield: boolean;
  bombPass: boolean;
  linePlacement: boolean;
  punch: boolean;
  alive: boolean;
  health: number;
  deathTick?: number;
  invulnerableTicks: number;
  spawnProtectionTicks: number;
  score: number;
  lastInput: NormalizedInputCommand;
}

interface MutableBomb {
  id: number;
  ownerId: PlayerId;
  cell: Cell;
  fuseRemaining: number;
  range: number;
  remote: boolean;
  pierce: boolean;
  placedTick: number;
}

interface MutableItem {
  id: string;
  type: ItemPlacement["type"];
  cell: Cell;
}

interface MutableFlame {
  cell: Cell;
  remaining: number;
  sourceBombId?: number;
}

interface MutableState {
  tick: number;
  map: MutableArenaMap;
  baseMap: ArenaMap;
  players: MutablePlayer[];
  bombs: MutableBomb[];
  items: MutableItem[];
  flames: MutableFlame[];
  suddenDeathHazards: Set<string>;
  suddenDeath: boolean;
  suddenDeathElapsed: number;
  suddenDeathRingIndex: number;
  status: RoundStatus;
  winnerId?: PlayerId;
  roundTicksRemaining: number;
  nextBombId: number;
  nextItemId: number;
  rng: SeededRng;
  events: GameEvent[];
  replayInputs: ReplayTick[];
  replayEvents: GameEvent[];
  replayDigest: string;
  hadDeath: boolean;
}

interface MovementPlan {
  readonly player: MutablePlayer;
  readonly direction: Direction;
  readonly target: Cell;
}

const EMPTY_INPUT: NormalizedInputCommand = Object.freeze({
  up: false,
  down: false,
  left: false,
  right: false,
  bomb: false,
  detonate: false,
  kick: false,
  punch: false,
  line: false,
});

const DIRECTION_ORDER: readonly Direction[] = Object.freeze([
  "up",
  "down",
  "left",
  "right",
]);
const RAY_DIRECTIONS: readonly Cell[] = Object.freeze([
  Object.freeze({ x: 0, y: -1 }),
  Object.freeze({ x: 0, y: 1 }),
  Object.freeze({ x: -1, y: 0 }),
  Object.freeze({ x: 1, y: 0 }),
]);

const ITEM_WEIGHTS: readonly { type: ItemPlacement["type"]; weight: number }[] =
  Object.freeze([
    Object.freeze({ type: "bomb", weight: 25 }),
    Object.freeze({ type: "fire", weight: 25 }),
    Object.freeze({ type: "speed", weight: 20 }),
    Object.freeze({ type: "kick", weight: 10 }),
    Object.freeze({ type: "remote", weight: 8 }),
    Object.freeze({ type: "pierce", weight: 7 }),
    Object.freeze({ type: "shield", weight: 5 }),
  ]);

const ITEM_WEIGHT_TOTAL = ITEM_WEIGHTS.reduce(
  (total, entry) => total + entry.weight,
  0,
);

function compareIds(left: { id: string }, right: { id: string }): number {
  if (left.id < right.id) {
    return -1;
  }
  if (left.id > right.id) {
    return 1;
  }
  return 0;
}

function compareCells(left: Cell, right: Cell): number {
  return left.y - right.y || left.x - right.x;
}

function directionVector(direction: Direction): Cell {
  switch (direction) {
    case "up":
      return { x: 0, y: -1 };
    case "down":
      return { x: 0, y: 1 };
    case "left":
      return { x: -1, y: 0 };
    case "right":
      return { x: 1, y: 0 };
  }
}

function addCell(left: Cell, right: Cell): Cell {
  return { x: left.x + right.x, y: left.y + right.y };
}

function cellFromPosition(position: MutablePoint): Cell {
  return {
    x: Math.round(position.x / POSITION_SCALE),
    y: Math.round(position.y / POSITION_SCALE),
  };
}

function logicalPosition(position: MutablePoint): Cell {
  return {
    x: position.x / POSITION_SCALE,
    y: position.y / POSITION_SCALE,
  };
}

function normalizeRules(config: GameConfig): GameRules {
  const configured = config.rules ?? {};
  const rules: GameRules = {
    ...DEFAULT_RULES,
    ...configured,
    rulesetVersion:
      config.rulesetVersion ??
      configured.rulesetVersion ??
      DEFAULT_RULES.rulesetVersion,
    contentVersion:
      config.contentVersion ??
      configured.contentVersion ??
      DEFAULT_RULES.contentVersion,
  };

  const positiveIntegerRules: readonly (keyof GameRules)[] = [
    "tickRate",
    "arenaWidth",
    "arenaHeight",
    "maxPlayers",
    "bombFuseTicks",
    "flameLifetimeTicks",
    "defaultBombRange",
    "defaultBombCapacity",
    "defaultPlayerSpeed",
    "speedIncrement",
    "maxSpeedLevel",
    "maxBombCapacity",
    "maxBombRange",
    "spawnProtectionTicks",
    "shieldInvulnerabilityTicks",
    "roundTicks",
    "suddenDeathIntervalTicks",
  ];
  const zeroAllowedRules: readonly (keyof GameRules)[] = [
    "roundTicks",
    "spawnProtectionTicks",
    "shieldInvulnerabilityTicks",
  ];
  for (const key of positiveIntegerRules) {
    const value = rules[key] as number;
    if (
      !Number.isInteger(value) ||
      value < 0 ||
      (!zeroAllowedRules.includes(key) && value === 0)
    ) {
      throw new RangeError(
        `${key} must be a positive integer${zeroAllowedRules.includes(key) ? " or zero" : ""}`,
      );
    }
  }
  if (rules.maxPlayers > 8) {
    throw new RangeError("maxPlayers cannot exceed eight");
  }
  if (rules.defaultBombCapacity > rules.maxBombCapacity) {
    throw new RangeError("defaultBombCapacity cannot exceed maxBombCapacity");
  }
  if (rules.defaultBombRange > rules.maxBombRange) {
    throw new RangeError("defaultBombRange cannot exceed maxBombRange");
  }
  if (
    rules.itemDropChance < 0 ||
    rules.itemDropChance > 1 ||
    !Number.isFinite(rules.itemDropChance)
  ) {
    throw new RangeError("itemDropChance must be between zero and one");
  }
  if (
    rules.destructibleDensity < 0 ||
    rules.destructibleDensity > 1 ||
    !Number.isFinite(rules.destructibleDensity)
  ) {
    throw new RangeError("destructibleDensity must be between zero and one");
  }
  return Object.freeze(rules);
}

export function normalizeInputCommand(
  command: InputCommand = {},
): NormalizedInputCommand {
  const candidate = command as Record<string, unknown>;
  const booleanKeys = [
    "up",
    "down",
    "left",
    "right",
    "bomb",
    "action",
    "detonate",
    "kick",
    "punch",
    "line",
  ] as const;
  for (const key of booleanKeys) {
    if (candidate[key] !== undefined && typeof candidate[key] !== "boolean") {
      throw new TypeError(`${key} input must be boolean`);
    }
  }
  const explicitDirection = candidate.direction ?? candidate.move;
  if (
    explicitDirection !== undefined &&
    explicitDirection !== "up" &&
    explicitDirection !== "down" &&
    explicitDirection !== "left" &&
    explicitDirection !== "right"
  ) {
    throw new TypeError("direction input must be up, down, left, or right");
  }
  return Object.freeze({
    up: candidate.up === true || explicitDirection === "up",
    down: candidate.down === true || explicitDirection === "down",
    left: candidate.left === true || explicitDirection === "left",
    right: candidate.right === true || explicitDirection === "right",
    bomb: candidate.bomb === true || candidate.action === true,
    detonate: candidate.detonate === true,
    kick: candidate.kick === true,
    punch: candidate.punch === true,
    line: candidate.line === true,
  });
}

function chooseDirection(input: NormalizedInputCommand): Direction | undefined {
  for (const direction of DIRECTION_ORDER) {
    if (input[direction]) {
      return direction;
    }
  }
  return undefined;
}

function inputFrameFromValue(
  value: InputFrame | readonly PlayerInput[] | TickInputFrame | undefined,
  tick: number,
  playerIds: ReadonlySet<string>,
): Record<string, InputCommand> {
  if (value === undefined) {
    return {};
  }
  if (Array.isArray(value)) {
    const frame: Record<string, InputCommand> = {};
    for (const entry of value) {
      if (playerIds.has(entry.playerId)) {
        frame[entry.playerId] = entry.command;
      }
    }
    return frame;
  }
  if (typeof value === "object" && value !== null && "inputs" in value) {
    const tickFrame = value as TickInputFrame;
    if (tickFrame.tick !== tick) {
      throw new RangeError(
        `Input frame targets tick ${tickFrame.tick}, expected ${tick}`,
      );
    }
    return inputFrameFromValue(tickFrame.inputs, tick, playerIds);
  }
  return value as InputFrame;
}

function copyInputFrame(
  value: Record<string, InputCommand>,
  players: readonly MutablePlayer[],
): { normalized: Record<string, NormalizedInputCommand>; replay: InputFrame } {
  const normalized: Record<string, NormalizedInputCommand> = {};
  for (const player of players) {
    normalized[player.id] = normalizeInputCommand(value[player.id]);
  }
  return { normalized, replay: normalized };
}

function currentCell(player: MutablePlayer): Cell {
  return cellFromPosition(player.position);
}

function sortedPlayers(players: readonly MutablePlayer[]): MutablePlayer[] {
  return [...players].sort(compareIds);
}

function sortedBombs(bombs: readonly MutableBomb[]): MutableBomb[] {
  return [...bombs].sort((left, right) => left.id - right.id);
}

function findBombAt(
  bombs: readonly MutableBomb[],
  cell: Cell,
): MutableBomb | undefined {
  return bombs.find((bomb) => bomb.cell.x === cell.x && bomb.cell.y === cell.y);
}

function findItemAt(
  items: readonly MutableItem[],
  cell: Cell,
): MutableItem | undefined {
  return items.find((item) => item.cell.x === cell.x && item.cell.y === cell.y);
}

function sameCell(left: Cell, right: Cell): boolean {
  return left.x === right.x && left.y === right.y;
}

function isCentered(value: number): boolean {
  return value % POSITION_SCALE === 0;
}

function moveToward(value: number, target: number, amount: number): number {
  if (value === target) {
    return value;
  }
  const difference = target - value;
  const step = Math.min(Math.abs(difference), amount);
  return value + Math.sign(difference) * step;
}

function itemTypeFromRng(rng: SeededRng): ItemPlacement["type"] {
  let selection = rng.nextInt(ITEM_WEIGHT_TOTAL);
  for (const entry of ITEM_WEIGHTS) {
    if (selection < entry.weight) {
      return entry.type;
    }
    selection -= entry.weight;
  }
  return "bomb";
}

function buildSuddenDeathOrder(width: number, height: number): readonly Cell[] {
  const cells: Cell[] = [];
  const maximumRing = Math.floor(Math.min(width, height) / 2);
  for (let ring = 0; ring <= maximumRing; ring += 1) {
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const distance = Math.min(x, y, width - 1 - x, height - 1 - y);
        if (distance === ring) {
          cells.push({ x, y });
        }
      }
    }
  }
  return cells;
}

export class GameCore {
  public readonly seed: number;
  public readonly rules: GameRules;
  private readonly playerConfigs: readonly PlayerConfig[];
  private readonly initialItems: readonly ItemPlacement[];
  private readonly suddenDeathOrder: readonly Cell[];
  private state: MutableState;

  public constructor(configOrSeed: GameConfig | Seed = {}) {
    const config: GameConfig =
      typeof configOrSeed === "object" && configOrSeed !== null
        ? configOrSeed
        : { seed: configOrSeed };
    this.seed = hashSeed(config.seed ?? 1);
    this.rules = normalizeRules(config);

    const configuredPlayers: PlayerConfig[] = config.players
      ? [...config.players]
      : Array.from(
          { length: config.playerCount ?? 2 },
          (_, index): PlayerConfig => ({ id: `p${index + 1}` }),
        );
    if (
      configuredPlayers.length < 1 ||
      configuredPlayers.length > this.rules.maxPlayers ||
      configuredPlayers.some((player) => player.id.length === 0)
    ) {
      throw new RangeError(
        `game must contain between one and ${this.rules.maxPlayers} named players`,
      );
    }
    const ids = new Set<string>();
    for (const player of configuredPlayers) {
      if (ids.has(player.id)) {
        throw new RangeError(`duplicate player id ${player.id}`);
      }
      ids.add(player.id);
    }

    const mapOptions = {
      ...(config.mapOptions ?? {}),
      width: config.mapOptions?.width ?? this.rules.arenaWidth,
      height: config.mapOptions?.height ?? this.rules.arenaHeight,
      playerCount: configuredPlayers.length,
      destructibleDensity:
        config.mapOptions?.destructibleDensity ??
        this.rules.destructibleDensity,
    };
    const baseMap = config.map
      ? freezeArenaMap(cloneArenaMap(config.map))
      : generateMap(this.seed, mapOptions);
    const spawnPoints = getSpawnPoints(
      baseMap.width,
      baseMap.height,
      configuredPlayers.length,
    );
    const createdPlayers: MutablePlayer[] = [];
    const players: MutablePlayer[] = configuredPlayers.map(
      (configPlayer, index) => {
        const spawn = configPlayer.spawn ?? spawnPoints[index];
        if (
          spawn === undefined ||
          !isInside(baseMap, spawn) ||
          getTile(baseMap, spawn) !== "floor"
        ) {
          throw new RangeError(
            `player ${configPlayer.id} must spawn on a floor tile`,
          );
        }
        if (playersContainSpawn(createdPlayers, spawn)) {
          throw new RangeError(`players cannot share spawn ${cellKey(spawn)}`);
        }
        const fixed = {
          x: spawn.x * POSITION_SCALE,
          y: spawn.y * POSITION_SCALE,
        };
        const player: MutablePlayer = {
          id: configPlayer.id,
          ...(configPlayer.name === undefined
            ? {}
            : { name: configPlayer.name }),
          position: fixed,
          previousPosition: { ...fixed },
          facing: "down",
          speedLevel: 0,
          maxBombs: this.rules.defaultBombCapacity,
          bombRange: this.rules.defaultBombRange,
          kick: false,
          remote: false,
          pierce: false,
          shield: false,
          bombPass: configPlayer.bombPass === true,
          linePlacement: configPlayer.linePlacement === true,
          punch: configPlayer.punch === true,
          alive: true,
          health: 1,
          invulnerableTicks: 0,
          spawnProtectionTicks: this.rules.spawnProtectionTicks,
          score: 0,
          lastInput: EMPTY_INPUT,
        };
        createdPlayers.push(player);
        return player;
      },
    );
    players.sort(compareIds);

    const initialItems: ItemPlacement[] = (config.initialItems ?? []).map(
      (item) => ({
        type: item.type,
        cell: { ...item.cell },
        ...(item.id === undefined ? {} : { id: item.id }),
      }),
    );
    const itemIds = new Set<string>();
    let nextItemId = 1;
    const createdItems: MutableItem[] = [];
    const items: MutableItem[] = initialItems.map((item) => {
      if (
        !isInside(baseMap, item.cell) ||
        getTile(baseMap, item.cell) !== "floor"
      ) {
        throw new RangeError(
          `item ${item.id ?? item.type} must be placed on a floor tile`,
        );
      }
      let id = item.id ?? `item-${nextItemId}`;
      if (item.id !== undefined && itemIds.has(id)) {
        throw new RangeError(`duplicate item id ${id}`);
      }
      while (item.id === undefined && itemIds.has(id)) {
        nextItemId += 1;
        id = `item-${nextItemId}`;
      }
      itemIds.add(id);
      nextItemId += 1;
      if (itemsContainCell(createdItems, item.cell)) {
        throw new RangeError(`items cannot share cell ${cellKey(item.cell)}`);
      }
      const created = { id, type: item.type, cell: { ...item.cell } };
      createdItems.push(created);
      return created;
    });

    this.playerConfigs = players.map((player) => ({
      id: player.id,
      ...(player.name === undefined ? {} : { name: player.name }),
      spawn: currentCell(player),
      ...(player.bombPass ? { bombPass: true } : {}),
      ...(player.linePlacement ? { linePlacement: true } : {}),
      ...(player.punch ? { punch: true } : {}),
    }));
    this.initialItems = initialItems.map((item, index) => ({
      ...item,
      ...(items[index]?.id === undefined ? {} : { id: items[index]?.id }),
      cell: { ...item.cell },
    }));
    this.suddenDeathOrder = buildSuddenDeathOrder(
      baseMap.width,
      baseMap.height,
    );
    this.state = {
      tick: 0,
      map: cloneArenaMap(baseMap),
      baseMap,
      players,
      bombs: [],
      items,
      flames: [],
      suddenDeathHazards: new Set<string>(),
      suddenDeath: false,
      suddenDeathElapsed: 0,
      suddenDeathRingIndex: 0,
      status: "running",
      roundTicksRemaining: this.rules.roundTicks,
      nextBombId: 1,
      nextItemId,
      rng: new SeededRng(mixSeed(this.seed, "runtime")),
      events: [],
      replayInputs: [],
      replayEvents: [],
      replayDigest: hashCanonical({
        seed: this.seed,
        rulesetVersion: this.rules.rulesetVersion,
        contentVersion: this.rules.contentVersion,
      }),
      hadDeath: false,
    };
    this.recordEvent({ type: "round-started" });
  }

  public static fromReplay(replay: ReplayData): GameCore {
    if (replay.formatVersion !== 1) {
      throw new RangeError(`Unsupported replay format ${replay.formatVersion}`);
    }
    const game = new GameCore({
      seed: replay.seed,
      map: replay.map,
      rules: replay.rules,
      players: replay.players,
      initialItems: replay.initialItems,
      contentVersion: replay.contentVersion,
      rulesetVersion: replay.rulesetVersion,
    });
    for (const tick of replay.inputs) {
      if (tick.tick !== game.state.tick) {
        throw new RangeError(
          `Replay input tick ${tick.tick} does not match game tick ${game.state.tick}`,
        );
      }
      game.step(tick.inputs);
    }
    return game;
  }

  public get tick(): number {
    return this.state.tick;
  }

  public get status(): RoundStatus {
    return this.state.status;
  }

  public getSnapshot(): GameSnapshot {
    const players = sortedPlayers(this.state.players).map((player) =>
      this.snapshotPlayer(player),
    );
    const bombs: BombSnapshot[] = sortedBombs(this.state.bombs).map((bomb) => ({
      id: bomb.id,
      ownerId: bomb.ownerId,
      cell: { ...bomb.cell },
      fuseRemaining: bomb.fuseRemaining,
      range: bomb.range,
      remote: bomb.remote,
      pierce: bomb.pierce,
      placedTick: bomb.placedTick,
    }));
    const flames: FlameSnapshot[] = [...this.state.flames]
      .sort((left, right) => compareCells(left.cell, right.cell))
      .map((flame) => ({
        cell: { ...flame.cell },
        remaining: flame.remaining,
        ...(flame.sourceBombId === undefined
          ? {}
          : { sourceBombId: flame.sourceBombId }),
      }));
    const items: ItemSnapshot[] = [...this.state.items]
      .sort(compareIds)
      .map((item) => ({
        id: item.id,
        type: item.type,
        cell: { ...item.cell },
      }));
    const suddenDeathHazards = [...this.state.suddenDeathHazards]
      .map((key) => this.cellFromKey(key))
      .sort(compareCells);

    const base = {
      tick: this.state.tick,
      seed: this.seed,
      rulesetVersion: this.rules.rulesetVersion,
      contentVersion: this.rules.contentVersion,
      map: freezeArenaMap(this.state.map),
      players,
      bombs,
      flames,
      items,
      suddenDeathHazards,
      status: this.state.status,
      ...(this.state.winnerId === undefined
        ? {}
        : { winnerId: this.state.winnerId }),
      roundTicksRemaining: this.state.roundTicksRemaining,
      suddenDeath: this.state.suddenDeath,
      rngState: this.state.rng.state,
      events: [...this.state.events],
    } satisfies Omit<GameSnapshot, "replayHash">;
    const snapshot: GameSnapshot = {
      ...base,
      replayHash: hashCanonical({
        ...base,
        replayDigest: this.state.replayDigest,
      }),
    };
    return deepFreezeSnapshot(snapshot);
  }

  public get snapshot(): GameSnapshot {
    return this.getSnapshot();
  }

  public step(
    input: InputFrame | readonly PlayerInput[] | TickInputFrame = {},
  ): GameSnapshot {
    if (this.state.status !== "running") {
      return this.getSnapshot();
    }

    const currentTick = this.state.tick;
    const playerIds = new Set(this.state.players.map((player) => player.id));
    const rawFrame = inputFrameFromValue(input, currentTick, playerIds);
    const { normalized, replay } = copyInputFrame(rawFrame, this.state.players);
    this.state.events = [];
    this.ageFlames();
    for (const player of this.state.players) {
      player.previousPosition = { ...player.position };
    }
    this.resolveMovement(normalized);
    const remoteRequests = this.resolveBombActions(normalized);
    const detonationQueue = this.advanceFuses(remoteRequests);
    this.resolveDetonations(detonationQueue);
    this.resolveWallsAndDrops();
    this.resolvePickups();
    this.resolveDamage();
    this.resolveRoundAndTimers();
    for (const player of this.state.players) {
      player.lastInput = normalized[player.id] ?? EMPTY_INPUT;
    }

    this.state.replayInputs.push({ tick: currentTick, inputs: replay });
    this.state.replayDigest = hashCanonical({
      previous: this.state.replayDigest,
      tick: currentTick,
      inputs: replay,
    });
    this.state.tick += 1;
    return this.getSnapshot();
  }

  public tickOnce(
    input: InputFrame | readonly PlayerInput[] | TickInputFrame = {},
  ): GameSnapshot {
    return this.step(input);
  }

  public advance(
    input: InputFrame | readonly PlayerInput[] | TickInputFrame = {},
  ): GameSnapshot {
    return this.step(input);
  }

  public run(
    ticks: number,
    inputProvider: (
      tick: number,
    ) => InputFrame | readonly PlayerInput[] | TickInputFrame = () => ({}),
  ): readonly GameSnapshot[] {
    if (!Number.isInteger(ticks) || ticks < 0) {
      throw new RangeError("ticks must be a non-negative integer");
    }
    const snapshots: GameSnapshot[] = [];
    for (let index = 0; index < ticks; index += 1) {
      snapshots.push(this.step(inputProvider(this.state.tick)));
      if (this.state.status !== "running") {
        break;
      }
    }
    return snapshots;
  }

  public getReplay(): ReplayData {
    const snapshot = this.getSnapshot();
    return deepFreezeSnapshot({
      formatVersion: 1,
      rulesetVersion: this.rules.rulesetVersion,
      contentVersion: this.rules.contentVersion,
      seed: this.seed,
      map: freezeArenaMap(this.state.baseMap),
      rules: this.rules,
      players: this.playerConfigs.map((player) => ({
        ...player,
        ...(player.spawn === undefined ? {} : { spawn: { ...player.spawn } }),
      })),
      initialItems: this.initialItems.map((item) => ({
        ...item,
        cell: { ...item.cell },
      })),
      inputs: this.state.replayInputs.map((entry) => ({
        tick: entry.tick,
        inputs: { ...entry.inputs },
      })),
      events: [...this.state.replayEvents],
      finalHash: snapshot.replayHash,
    });
  }

  public getInvariantViolations(): readonly InvariantViolation[] {
    return validateSnapshot(this.getSnapshot());
  }

  public assertInvariants(): void {
    assertSnapshotInvariants(this.getSnapshot());
  }

  public isValid(): boolean {
    return this.getInvariantViolations().length === 0;
  }

  private snapshotPlayer(player: MutablePlayer): PlayerSnapshot {
    const activeBombs = this.state.bombs.filter(
      (bomb) => bomb.ownerId === player.id,
    ).length;
    const position = logicalPosition(player.position);
    const previousPosition = logicalPosition(player.previousPosition);
    return {
      id: player.id,
      ...(player.name === undefined ? {} : { name: player.name }),
      x: position.x,
      y: position.y,
      position: { ...position },
      previousPosition: { ...previousPosition },
      cell: currentCell(player),
      alive: player.alive,
      health: player.health,
      ...(player.deathTick === undefined
        ? {}
        : { deathTick: player.deathTick }),
      facing: player.facing,
      speedLevel: player.speedLevel,
      speed: this.playerSpeed(player),
      maxBombs: player.maxBombs,
      activeBombs,
      bombsAvailable: player.maxBombs - activeBombs,
      bombRange: player.bombRange,
      kick: player.kick,
      remote: player.remote,
      pierce: player.pierce,
      shield: player.shield,
      bombPass: player.bombPass,
      linePlacement: player.linePlacement,
      punch: player.punch,
      invulnerableTicks: player.invulnerableTicks,
      spawnProtectionTicks: player.spawnProtectionTicks,
      score: player.score,
    };
  }

  private playerSpeed(player: MutablePlayer): number {
    return Math.min(
      this.rules.defaultPlayerSpeed +
        player.speedLevel * this.rules.speedIncrement,
      this.rules.defaultPlayerSpeed +
        this.rules.maxSpeedLevel * this.rules.speedIncrement,
    );
  }

  private resolveMovement(
    normalized: Readonly<Record<string, NormalizedInputCommand>>,
  ): void {
    const players = sortedPlayers(this.state.players).filter(
      (player) => player.alive,
    );
    this.resolveKicks(players, normalized);
    const plans: MovementPlan[] = [];
    for (const player of players) {
      const input = normalized[player.id] ?? EMPTY_INPUT;
      const direction = chooseDirection(input);
      if (direction === undefined) {
        continue;
      }
      player.facing = direction;
      const plan = this.planMovement(player, direction);
      if (plan !== undefined) {
        plans.push(plan);
      }
    }

    const plansByPlayer = new Map(plans.map((plan) => [plan.player.id, plan]));
    const targetWinners = new Map<string, MovementPlan>();
    for (const plan of plans) {
      const key = cellKey(plan.target);
      if (!targetWinners.has(key)) {
        targetWinners.set(key, plan);
      }
    }
    const winners = new Set(
      [...targetWinners.values()].map((plan) => plan.player.id),
    );
    let changed = true;
    while (changed) {
      changed = false;
      for (const plan of plans) {
        if (!winners.has(plan.player.id)) {
          continue;
        }
        const occupant = players.find(
          (other) =>
            other.id !== plan.player.id &&
            sameCell(currentCell(other), plan.target),
        );
        if (occupant === undefined) {
          continue;
        }
        const occupantPlan = plansByPlayer.get(occupant.id);
        if (
          occupantPlan === undefined ||
          !winners.has(occupant.id) ||
          sameCell(occupantPlan.target, currentCell(plan.player))
        ) {
          winners.delete(plan.player.id);
          changed = true;
        }
      }
    }

    for (const plan of plans) {
      if (winners.has(plan.player.id)) {
        this.applyMovement(plan.player, plan.direction, plan.target);
      }
    }
  }

  private resolveKicks(
    players: readonly MutablePlayer[],
    normalized: Readonly<Record<string, NormalizedInputCommand>>,
  ): void {
    const movedBombs = new Set<number>();
    for (const player of players) {
      if (!player.kick) {
        continue;
      }
      const direction = chooseDirection(normalized[player.id] ?? EMPTY_INPUT);
      if (direction === undefined) {
        continue;
      }
      const vector = directionVector(direction);
      const target = addCell(currentCell(player), vector);
      const bomb = findBombAt(this.state.bombs, target);
      if (bomb === undefined || movedBombs.has(bomb.id)) {
        continue;
      }
      const destination = addCell(target, vector);
      if (
        !isInside(this.state.map, destination) ||
        !isWalkableTile(getTile(this.state.map, destination)) ||
        findBombAt(this.state.bombs, destination) !== undefined ||
        this.state.players.some(
          (other) => other.alive && sameCell(currentCell(other), destination),
        )
      ) {
        continue;
      }
      bomb.cell = destination;
      movedBombs.add(bomb.id);
      this.recordEvent({
        type: "bomb-kicked",
        playerId: player.id,
        ownerId: bomb.ownerId,
        bombId: bomb.id,
        cell: { ...destination },
      });
    }
  }

  private planMovement(
    player: MutablePlayer,
    direction: Direction,
  ): MovementPlan | undefined {
    const vector = directionVector(direction);
    const position = player.position;
    if (vector.x !== 0 && !isCentered(position.y)) {
      return undefined;
    }
    if (vector.y !== 0 && !isCentered(position.x)) {
      return undefined;
    }

    const coordinate = vector.x !== 0 ? position.x : position.y;
    const baseIndex =
      vector.x + vector.y > 0
        ? Math.floor(coordinate / POSITION_SCALE)
        : Math.ceil(coordinate / POSITION_SCALE);
    const target = { ...currentCell(player) };
    if (vector.x !== 0) {
      target.x = baseIndex + vector.x;
    } else {
      target.y = baseIndex + vector.y;
    }
    if (
      !isInside(this.state.map, target) ||
      !isWalkableTile(getTile(this.state.map, target))
    ) {
      return undefined;
    }
    const bomb = findBombAt(this.state.bombs, target);
    if (bomb !== undefined && !player.bombPass) {
      return undefined;
    }
    return { player, direction, target };
  }

  private applyMovement(
    player: MutablePlayer,
    direction: Direction,
    target: Cell,
  ): void {
    const vector = directionVector(direction);
    const speed = this.playerSpeed(player);
    const targetPosition = {
      x: target.x * POSITION_SCALE,
      y: target.y * POSITION_SCALE,
    };
    if (vector.x !== 0) {
      player.position.y = moveToward(
        player.position.y,
        Math.round(player.position.y / POSITION_SCALE) * POSITION_SCALE,
        speed,
      );
      if (player.position.y !== targetPosition.y) {
        return;
      }
      player.position.x = moveToward(
        player.position.x,
        targetPosition.x,
        speed,
      );
    } else {
      player.position.x = moveToward(
        player.position.x,
        Math.round(player.position.x / POSITION_SCALE) * POSITION_SCALE,
        speed,
      );
      if (player.position.x !== targetPosition.x) {
        return;
      }
      player.position.y = moveToward(
        player.position.y,
        targetPosition.y,
        speed,
      );
    }
  }

  private resolveBombActions(
    normalized: Readonly<Record<string, NormalizedInputCommand>>,
  ): ReadonlySet<number> {
    const remoteRequests = new Set<number>();
    for (const player of sortedPlayers(this.state.players)) {
      if (!player.alive) {
        continue;
      }
      const input = normalized[player.id] ?? EMPTY_INPUT;
      const bombPressed = input.bomb && !player.lastInput.bomb;
      const linePressed = input.line && !player.lastInput.line;
      if (bombPressed || (linePressed && player.linePlacement)) {
        this.placeBombs(player, input.line && player.linePlacement);
      }
      if (input.punch && !player.lastInput.punch && player.punch) {
        this.resolvePunch(player);
      }
      if (input.detonate && !player.lastInput.detonate) {
        for (const bomb of this.state.bombs) {
          if (bomb.ownerId === player.id && bomb.remote) {
            remoteRequests.add(bomb.id);
          }
        }
      }
    }
    return remoteRequests;
  }

  private placeBombs(player: MutablePlayer, line: boolean): void {
    const available = player.maxBombs - this.countActiveBombs(player.id);
    if (available <= 0) {
      return;
    }
    const cells: Cell[] = [currentCell(player)];
    if (line) {
      const vector = directionVector(player.facing);
      for (let index = 1; index < available; index += 1) {
        const previous = cells[index - 1] as Cell;
        cells.push({ x: previous.x + vector.x, y: previous.y + vector.y });
      }
    }
    let placed = 0;
    for (const cell of cells) {
      if (
        placed >= available ||
        !isInside(this.state.map, cell) ||
        !isWalkableTile(getTile(this.state.map, cell))
      ) {
        break;
      }
      if (findBombAt(this.state.bombs, cell) !== undefined) {
        break;
      }
      const bomb: MutableBomb = {
        id: this.state.nextBombId,
        ownerId: player.id,
        cell: { ...cell },
        fuseRemaining: this.rules.bombFuseTicks,
        range: player.bombRange,
        remote: player.remote,
        pierce: player.pierce,
        placedTick: this.state.tick,
      };
      this.state.nextBombId += 1;
      this.state.bombs.push(bomb);
      placed += 1;
      this.recordEvent({
        type: "bomb-placed",
        playerId: player.id,
        ownerId: player.id,
        bombId: bomb.id,
        cell: { ...cell },
      });
    }
  }

  private resolvePunch(player: MutablePlayer): void {
    const vector = directionVector(player.facing);
    const target = addCell(currentCell(player), vector);
    if (
      !isInside(this.state.map, target) ||
      getTile(this.state.map, target) !== "destructible"
    ) {
      return;
    }
    this.state.map.tiles[target.y]![target.x] = "floor";
    player.score += 1;
    this.recordEvent({
      type: "wall-destroyed",
      playerId: player.id,
      cell: { ...target },
      reason: "punch",
    });
    this.maybeDropItem(target);
  }

  private countActiveBombs(ownerId: PlayerId): number {
    return this.state.bombs.reduce(
      (count, bomb) => count + (bomb.ownerId === ownerId ? 1 : 0),
      0,
    );
  }

  private advanceFuses(remoteRequests: ReadonlySet<number>): number[] {
    const queue: number[] = [];
    for (const bomb of sortedBombs(this.state.bombs)) {
      if (remoteRequests.has(bomb.id)) {
        queue.push(bomb.id);
        continue;
      }
      if (bomb.remote || bomb.placedTick >= this.state.tick) {
        continue;
      }
      bomb.fuseRemaining -= 1;
      if (bomb.fuseRemaining <= 0) {
        queue.push(bomb.id);
      }
    }
    return [...new Set(queue)].sort((left, right) => left - right);
  }

  private resolveDetonations(initialQueue: readonly number[]): void {
    const queue = [...initialQueue].sort((left, right) => left - right);
    const queued = new Set(queue);
    const destroyed = new Set<string>();
    const bombOwners = new Map<number, PlayerId>();
    const destroyedByBomb = new Map<string, number>();
    while (queue.length > 0) {
      const bombId = queue.shift() as number;
      const bombIndex = this.state.bombs.findIndex(
        (candidate) => candidate.id === bombId,
      );
      if (bombIndex < 0) {
        continue;
      }
      const bomb = this.state.bombs[bombIndex] as MutableBomb;
      this.state.bombs.splice(bombIndex, 1);
      bombOwners.set(bomb.id, bomb.ownerId);
      this.recordEvent({
        type: "bomb-detonated",
        bombId: bomb.id,
        ownerId: bomb.ownerId,
        cell: { ...bomb.cell },
      });
      this.addFlame(bomb.cell, bomb.id);
      const vectorRange = Math.max(0, bomb.range);
      for (const vector of RAY_DIRECTIONS) {
        let cell = { ...bomb.cell };
        for (let distance = 0; distance < vectorRange; distance += 1) {
          cell = addCell(cell, vector);
          if (!isInside(this.state.map, cell)) {
            break;
          }
          const tile = getTile(this.state.map, cell);
          if (tile === "indestructible" || tile === undefined) {
            break;
          }
          this.addFlame(cell, bomb.id);
          const targetBomb = findBombAt(this.state.bombs, cell);
          if (targetBomb !== undefined) {
            if (!queued.has(targetBomb.id)) {
              queued.add(targetBomb.id);
              queue.push(targetBomb.id);
              queue.sort((left, right) => left - right);
            }
            break;
          }
          if (tile === "destructible") {
            destroyed.add(cellKey(cell));
            if (!destroyedByBomb.has(cellKey(cell))) {
              destroyedByBomb.set(cellKey(cell), bomb.id);
            }
            if (!bomb.pierce) {
              break;
            }
          }
        }
      }
    }

    for (const key of [...destroyed].sort(compareCellKeys)) {
      const cell = this.cellFromKey(key);
      if (getTile(this.state.map, cell) !== "destructible") {
        continue;
      }
      this.state.map.tiles[cell.y]![cell.x] = "floor";
      const ownerId = bombOwners.get(destroyedByBomb.get(key) ?? -1);
      const owner = this.state.players.find((player) => player.id === ownerId);
      if (owner !== undefined) {
        owner.score += 1;
      }
      this.recordEvent({ type: "wall-destroyed", cell: { ...cell } });
      this.maybeDropItem(cell);
    }
  }

  private addFlame(cell: Cell, sourceBombId: number): void {
    const existing = this.state.flames.find((flame) =>
      sameCell(flame.cell, cell),
    );
    if (existing === undefined) {
      this.state.flames.push({
        cell: { ...cell },
        remaining: this.rules.flameLifetimeTicks,
        sourceBombId,
      });
      return;
    }
    existing.remaining = Math.max(
      existing.remaining,
      this.rules.flameLifetimeTicks,
    );
    existing.sourceBombId =
      existing.sourceBombId === undefined
        ? sourceBombId
        : Math.min(existing.sourceBombId, sourceBombId);
  }

  private maybeDropItem(cell: Cell): void {
    if (
      findItemAt(this.state.items, cell) !== undefined ||
      this.state.rng.nextFloat() >= this.rules.itemDropChance
    ) {
      return;
    }
    const item: MutableItem = {
      id: `item-${this.state.nextItemId}`,
      type: itemTypeFromRng(this.state.rng),
      cell: { ...cell },
    };
    this.state.nextItemId += 1;
    this.state.items.push(item);
    this.recordEvent({
      type: "item-spawned",
      itemId: item.id,
      itemType: item.type,
      cell: { ...cell },
    });
  }

  private resolveWallsAndDrops(): void {
    // Wall destruction is committed during resolveDetonations so all rays see the same map.
  }

  private resolvePickups(): void {
    for (const player of sortedPlayers(this.state.players).filter(
      (candidate) => candidate.alive,
    )) {
      const item = findItemAt(this.state.items, currentCell(player));
      if (item === undefined) {
        continue;
      }
      this.state.items = this.state.items.filter(
        (candidate) => candidate.id !== item.id,
      );
      switch (item.type) {
        case "bomb":
          player.maxBombs = Math.min(
            this.rules.maxBombCapacity,
            player.maxBombs + 1,
          );
          break;
        case "fire":
          player.bombRange = Math.min(
            this.rules.maxBombRange,
            player.bombRange + 1,
          );
          break;
        case "speed":
          player.speedLevel = Math.min(
            this.rules.maxSpeedLevel,
            player.speedLevel + 1,
          );
          break;
        case "kick":
          player.kick = true;
          break;
        case "remote":
          player.remote = true;
          break;
        case "pierce":
          player.pierce = true;
          break;
        case "shield":
          player.shield = true;
          break;
      }
      this.recordEvent({
        type: "item-collected",
        playerId: player.id,
        itemId: item.id,
        itemType: item.type,
        cell: { ...item.cell },
      });
    }
  }

  private resolveDamage(): void {
    const flameCells = new Set(
      this.state.flames.map((flame) => cellKey(flame.cell)),
    );
    for (const player of sortedPlayers(this.state.players).filter(
      (candidate) => candidate.alive,
    )) {
      const dangerous = this.occupiedCells(player).some(
        (cell) =>
          flameCells.has(cellKey(cell)) ||
          this.state.suddenDeathHazards.has(cellKey(cell)),
      );
      if (
        !dangerous ||
        player.spawnProtectionTicks > 0 ||
        player.invulnerableTicks > 0
      ) {
        continue;
      }
      if (player.shield) {
        player.shield = false;
        player.invulnerableTicks = this.rules.shieldInvulnerabilityTicks;
        this.recordEvent({
          type: "player-damaged",
          playerId: player.id,
          reason: "shield",
        });
        continue;
      }
      player.health = 0;
      player.alive = false;
      player.deathTick = this.state.tick;
      this.state.hadDeath = true;
      this.recordEvent({
        type: "player-damaged",
        playerId: player.id,
        reason: "blast",
      });
      this.recordEvent({
        type: "player-died",
        playerId: player.id,
        reason: "blast",
      });
    }
  }

  private occupiedCells(player: MutablePlayer): readonly Cell[] {
    const cells = new Map<string, Cell>();
    const add = (cell: Cell): void => {
      if (isInside(this.state.map, cell)) {
        cells.set(cellKey(cell), cell);
      }
    };
    const rounded = currentCell(player);
    add(rounded);
    if (!isCentered(player.position.x)) {
      add({ x: Math.floor(player.position.x / POSITION_SCALE), y: rounded.y });
      add({ x: Math.ceil(player.position.x / POSITION_SCALE), y: rounded.y });
    }
    if (!isCentered(player.position.y)) {
      add({ x: rounded.x, y: Math.floor(player.position.y / POSITION_SCALE) });
      add({ x: rounded.x, y: Math.ceil(player.position.y / POSITION_SCALE) });
    }
    return [...cells.values()];
  }

  private resolveRoundAndTimers(): void {
    for (const player of this.state.players) {
      if (player.invulnerableTicks > 0) {
        player.invulnerableTicks -= 1;
      }
      if (player.spawnProtectionTicks > 0) {
        player.spawnProtectionTicks -= 1;
      }
    }

    const alive = this.state.players.filter((player) => player.alive);
    if (this.state.hadDeath && alive.length <= 1) {
      this.endRound(alive.length === 1 ? "won" : "draw", alive[0]?.id);
      return;
    }

    if (this.state.roundTicksRemaining > 0) {
      this.state.roundTicksRemaining -= 1;
    }
    if (this.state.roundTicksRemaining === 0 && !this.state.suddenDeath) {
      if (!this.rules.enableSuddenDeath) {
        this.endRound("draw");
        return;
      }
      this.state.suddenDeath = true;
      this.state.suddenDeathElapsed = 0;
      this.recordEvent({ type: "sudden-death-started" });
    } else if (this.state.suddenDeath) {
      this.state.suddenDeathElapsed += 1;
      if (
        this.state.suddenDeathElapsed % this.rules.suddenDeathIntervalTicks ===
        0
      ) {
        this.addSuddenDeathCell();
      }
    }
  }

  private addSuddenDeathCell(): void {
    while (this.state.suddenDeathRingIndex < this.suddenDeathOrder.length) {
      const cell = this.suddenDeathOrder[
        this.state.suddenDeathRingIndex
      ] as Cell;
      this.state.suddenDeathRingIndex += 1;
      if (
        !isInside(this.state.map, cell) ||
        getTile(this.state.map, cell) !== "floor"
      ) {
        continue;
      }
      const key = cellKey(cell);
      if (this.state.suddenDeathHazards.has(key)) {
        continue;
      }
      this.state.suddenDeathHazards.add(key);
      this.recordEvent({ type: "sudden-death-cell", cell: { ...cell } });
      return;
    }
  }

  private endRound(
    status: Exclude<RoundStatus, "running">,
    winnerId?: PlayerId,
  ): void {
    if (this.state.status !== "running") {
      return;
    }
    this.state.status = status;
    if (winnerId === undefined) {
      delete this.state.winnerId;
    } else {
      this.state.winnerId = winnerId;
    }
    this.recordEvent({
      type: "round-ended",
      ...(winnerId === undefined ? {} : { playerId: winnerId }),
      reason: status,
    });
  }

  private ageFlames(): void {
    const aliveFlames: MutableFlame[] = [];
    for (const flame of this.state.flames) {
      flame.remaining -= 1;
      if (flame.remaining > 0) {
        aliveFlames.push(flame);
      }
    }
    this.state.flames = aliveFlames;
  }

  private recordEvent(event: Omit<GameEvent, "tick">): void {
    const complete: GameEvent = { tick: this.state?.tick ?? 0, ...event };
    this.state?.events.push(complete);
    this.state?.replayEvents.push(complete);
  }

  private cellFromKey(key: string): Cell {
    const separator = key.indexOf(",");
    return {
      x: Number(key.slice(0, separator)),
      y: Number(key.slice(separator + 1)),
    };
  }
}

function compareCellKeys(left: string, right: string): number {
  const leftSeparator = left.indexOf(",");
  const rightSeparator = right.indexOf(",");
  const leftY = Number(left.slice(leftSeparator + 1));
  const rightY = Number(right.slice(rightSeparator + 1));
  const leftX = Number(left.slice(0, leftSeparator));
  const rightX = Number(right.slice(0, rightSeparator));
  return leftY - rightY || leftX - rightX;
}

function playersContainSpawn(
  players: readonly MutablePlayer[],
  spawn: Cell,
): boolean {
  return players.some((player) => sameCell(currentCell(player), spawn));
}

function itemsContainCell(items: readonly MutableItem[], cell: Cell): boolean {
  return items.some((item) => sameCell(item.cell, cell));
}

function deepFreezeSnapshot<T>(snapshot: T): T {
  if (snapshot === null || typeof snapshot !== "object") {
    return snapshot;
  }
  Object.freeze(snapshot);
  for (const value of Object.values(snapshot as Record<string, unknown>)) {
    if (
      value !== null &&
      typeof value === "object" &&
      !Object.isFrozen(value)
    ) {
      deepFreezeSnapshot(value);
    }
  }
  return snapshot;
}

export function createGame(configOrSeed: GameConfig | Seed = {}): GameCore {
  return new GameCore(configOrSeed);
}

export function createGameFromReplay(replay: ReplayData): GameCore {
  return GameCore.fromReplay(replay);
}

export function replayGame(replay: ReplayData): GameSnapshot {
  return GameCore.fromReplay(replay).getSnapshot();
}

export function runReplay(replay: ReplayData): GameCore {
  return GameCore.fromReplay(replay);
}

export { GameCore as Game };
export const createSimulation = createGame;

export type {
  GameConfig,
  InputCommand,
  InputFrame,
  PlayerInput,
  TickInputFrame,
};
